import type { PartnerName } from '@/components/home/partner-wordmark'
import type { ScriptLine } from '@/components/generals/chat/use-chat'

export const heroTicker: Array<string> = [
    "412,908 shipments in motion",
    "31 holds cleared in the last hour",
    "9 reroutes today"
]

export const partnerRows: Array<Array<PartnerName>> = [
    ['Northgate Provisions', 'Halden & Rowe', 'Kestrel Home', 'Volta Cycles', 'Brightwater Foods'],
    ['Ostrava Steel', 'Pinecrest Outdoor', 'Lumen Appliances', 'Marlow Textiles', 'Tandem Bikes']
]

export interface FullServiceTab {
    id: string
    label: string
    lead: string
    rest: string
    docLines: Array<string>
    conversation: Array<ScriptLine>
}

export const fullServiceTabs: Array<FullServiceTab> = [
    {
        id: 'requirements',
        label: "Requirements",
        lead: "Most freight integration starts as an operational problem, not a perfect spec.",
        rest: "Our solutions engineers ask the right questions about your lanes, carriers, customs steps and systems, then turn them into a clear plan.",
        docLines: ['Workflow delivery', 'Bookings to ERP', 'Initial carrier, lane and tariff migration'],
        conversation: [
            { side: 'left', text: "What kind of flow are you trying to build?" },
            { side: 'right', text: "I need our carrier bookings and vessel ETAs in our ERP, and visible to customers in the portal." },
            { side: 'left', text: "Got it. Your ERP owns the order record, and your customers need live status in the portal." }
        ]
    },
    {
        id: 'build',
        label: "Build",
        lead: "We build and test every connection against real bookings before anything touches production.",
        rest: "You see each lane working in a sandbox, with plain-language notes on what happens when something goes wrong.",
        docLines: ['Workflow delivery', 'ETA sync to customer portal', 'Sandbox testing on live bookings'],
        conversation: [
            { side: 'left', text: "The first lane is connected. Want to test it with a real booking?" },
            { side: 'right', text: "Yes, send one from Rotterdam to Chicago." },
            { side: 'left', text: "Done. The confirmation reached your ERP in 4 seconds and the portal shows ETA Friday." }
        ]
    },
    {
        id: 'launch',
        label: "Launch and support",
        lead: "Go-live happens with your team, not to your team.",
        rest: "We watch the first shipments together, then our operations desk takes over monitoring, support and long-term reliability.",
        docLines: ['Workflow delivery', 'Customs hold alerts', 'Monitoring and escalation'],
        conversation: [
            { side: 'left', text: "You're live. We're watching your first 500 shipments with you." },
            { side: 'right', text: "What happens if a carrier feed drops at night?" },
            { side: 'left', text: "Our ops desk catches it, switches to the backup feed and only messages you if it needs a decision." }
        ]
    }
]

export interface CustomerStory {
    company: PartnerName
    lane: string
    industry: string
    quote: string
    name: string
    role: string
    stat: {
        value: string
        label: string
    }
}

export const customerStories: Array<CustomerStory> = [
    {
        company: 'Northgate Provisions',
        lane: "RTM → CHI",
        industry: "Food distribution",
        quote: "Quayside connected our customer portal to our ERP and now about 95% of inbound bookings land without anyone touching them.",
        name: "Shawn Wilson",
        role: "Director of Operations",
        stat: { value: "95%", label: "of inbound bookings land untouched" }
    },
    {
        company: 'Volta Cycles',
        lane: "KHH → RTM",
        industry: "Bikes and e-commerce",
        quote: "We had eight weeks until peak and four systems that ignored each other. Quayside had the first lane live in eleven days.",
        name: "Ines Duarte",
        role: "Head of Logistics",
        stat: { value: "11 days", label: "from kickoff to live" }
    },
    {
        company: 'Kestrel Home',
        lane: "SHA → LGB",
        industry: "Home furnishings",
        quote: "Quickest turnaround on a systems integration we have ever had, and the most cost-effective.",
        name: "Roger Quach",
        role: "Chief Operating Officer",
        stat: { value: "19 days", label: "from first call to production" }
    },
    {
        company: 'Brightwater Foods',
        lane: "SIN → RTM",
        industry: "Food and beverage",
        quote: "We had four disconnected systems and endless double entry. Quayside tied them together and the duplicate work simply stopped.",
        name: "Heather Jordan",
        role: "Director of IT",
        stat: { value: "32h", label: "of double entry gone every week" }
    },
    {
        company: 'Halden & Rowe',
        lane: "HAM → NYC",
        industry: "Specialty retail",
        quote: "They have run our integration work for four years and the platform alone beat the two tools we used before.",
        name: "Bryan Ballard",
        role: "VP of Information Technology",
        stat: { value: "4 yrs", label: "running every integration they have" }
    },
    {
        company: 'Lumen Appliances',
        lane: "NGB → FXT",
        industry: "Manufacturing",
        quote: "The flexibility and the support are what stood out. Our customs and ERP flow fits how we actually work.",
        name: "Bill Stok",
        role: "Director of Finance",
        stat: { value: "100%", label: "of customs entries matched in the ERP" }
    }
]

export interface ActivityRow {
    value: number
    label: string
    time: string
}

export const activityFeed: Array<ActivityRow> = [
    { value: 56, label: "containers released", time: "36m ago" },
    { value: 79, label: "gate appointments rebooked", time: "1h ago" },
    { value: 11, label: "customs holds cleared", time: "3h ago" },
    { value: 34, label: "ETAs revised", time: "1d ago" },
    { value: 13, label: "bookings confirmed", time: "2m ago" },
    { value: 59, label: "invoices matched", time: "14m ago" },
    { value: 82, label: "pallets reallocated", time: "1h ago" },
    { value: 411, label: "proofs of delivery received", time: "1min ago" },
    { value: 143, label: "vessel schedules synced", time: "4m ago" }
]

export interface AnnualFigure {
    label: string
    value: string
}

export const annualFigures: Array<AnnualFigure> = [
    { label: "Events processed", value: "1,846,203,517" },
    { label: "Shipments tracked", value: "412.9M" },
    { label: "Hours saved", value: "9.6M" },
    { label: "Demurrage avoided", value: "$1.1B" }
]
